import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SharedLibrary = () => {
  const { libraryToken } = useParams();
  const { user, login } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  const [savedVolumes, setSavedVolumes] = useState(new Set());

  useEffect(() => {
    const fetchLibrary = async () => {
      try {
        const res = await axios.get(`${API}/shared/library/${libraryToken}`);
        setData(res.data);
      } catch (e) {
        setError(e.response?.status === 404 ? "This shared library link is invalid or has expired." : "Failed to load shared library.");
      } finally {
        setLoading(false);
      }
    };
    fetchLibrary();
  }, [libraryToken]);

  const handleSaveVolume = async (shareToken, name) => {
    if (!user) { login(); return; }
    try {
      await axios.post(`${API}/shared/${shareToken}/save`);
      setSavedVolumes(prev => new Set([...prev, shareToken]));
      toast.success(`"${name}" saved to your Ryan`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save volume");
    }
  };

  const handleSaveAll = async () => {
    if (!user) { login(); return; }
    setSavingAll(true);
    let saved = 0;
    for (const col of data.collections) {
      if (savedVolumes.has(col.share_token)) continue;
      try {
        await axios.post(`${API}/shared/${col.share_token}/save`);
        setSavedVolumes(prev => new Set([...prev, col.share_token]));
        saved++;
      } catch (e) {
        // Skip on error (e.g., already saved)
      }
    }
    toast.success(`${saved} volume${saved !== 1 ? "s" : ""} saved to your Ryan`);
    setSavingAll(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0600] flex items-center justify-center">
        <div className="spinner-ember" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0600] flex flex-col items-center justify-center text-center px-4">
        <div className="text-4xl mb-4 text-[#4A3A1A]">x</div>
        <h1 className="font-serif text-xl font-bold text-[#FDF8EE] mb-2">{error}</h1>
        <Link to="/" className="font-mono text-[10px] text-[#E8A030] hover:underline uppercase tracking-[1px] mt-4">
          Go to Ryan
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0600]" data-testid="shared-library-page">
      {/* Header */}
      <header className="border-b border-[rgba(232,160,48,0.1)] bg-[#1C1004]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="w-[9px] h-[9px] bg-[#E8A030] rotate-45" />
            <span className="font-serif font-black text-lg text-[#FDF8EE]">Ryan</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="font-mono text-[8px] text-[#4A3A1A] uppercase tracking-[1px]">Shared Library</span>
            {data.collections.length > 0 && (
              <button
                onClick={handleSaveAll}
                disabled={savingAll || savedVolumes.size === data.collections.length}
                className="btn-primary text-xs py-1.5 px-4"
                data-testid="save-all-btn"
              >
                {savingAll ? "Saving..." : savedVolumes.size === data.collections.length ? "All Saved" : user ? "Save All to my Ryan" : "Sign in to Save"}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-10">
          <h1 className="font-serif text-3xl font-bold text-[#FDF8EE] mb-2" data-testid="library-owner-name">
            {data.owner_name}'s Library
          </h1>
          <p className="font-mono text-[10px] text-[#4A3A1A] uppercase tracking-[1px]">
            {data.total_volumes} shared volume{data.total_volumes !== 1 ? "s" : ""}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.collections.map((col) => (
            <div
              key={col.collection_id}
              className="bg-[#1C1004] border border-[rgba(232,160,48,0.1)] p-6 hover:border-[rgba(232,160,48,0.25)] transition-colors group"
              data-testid={`library-volume-${col.collection_id}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <span style={{ color: col.color || "#E8A030" }} className="text-sm">&#9670;</span>
                <h3 className="font-serif font-bold text-[#FDF8EE] text-base truncate">{col.name}</h3>
              </div>
              {col.description && (
                <p className="text-[12px] text-[#8C7A5A] leading-[1.6] mb-3 line-clamp-2">{col.description}</p>
              )}
              <div className="font-mono text-[8px] text-[#4A3A1A] uppercase tracking-[1px] mb-4">
                {col.bookmark_count} entr{col.bookmark_count !== 1 ? "ies" : "y"}
              </div>
              <div className="flex gap-2">
                <Link
                  to={`/shared/${col.share_token}`}
                  className="btn-ghost text-xs py-1.5 px-3 flex-1 text-center"
                >
                  View
                </Link>
                {!savedVolumes.has(col.share_token) ? (
                  <button
                    onClick={() => handleSaveVolume(col.share_token, col.name)}
                    className="btn-primary text-xs py-1.5 px-3 flex-1"
                    data-testid={`save-volume-${col.collection_id}`}
                  >
                    Save
                  </button>
                ) : (
                  <span className="flex-1 text-center font-mono text-[9px] text-[#7AB8A0] py-1.5">Saved</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {data.collections.length === 0 && (
          <div className="text-center py-16">
            <p className="text-[#8C7A5A]">This library has no shared volumes yet.</p>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 text-center border-t border-[rgba(232,160,48,0.1)] pt-8">
          <p className="text-sm text-[#8C7A5A] mb-4">Build your own AI encyclopedia with Ryan.</p>
          <Link to="/" className="btn-primary inline-block" data-testid="library-cta">
            Get Started Free
          </Link>
        </div>
      </main>
    </div>
  );
};

export default SharedLibrary;
